import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import LocationMixedChart from '@/components/shared/charts/LocationMixedChart'
import { SegmentedControl } from '@/components/shared/layout'
import { getCountryEmoji } from '@/lib/utils/locationUtils'
import { countryCodes } from '@/lib/constants/countries'
import { aggregateCities, aggregateCountries, aggregateRegions } from '@/lib/calculations/breakdownTotals'
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowRight01Icon } from "@hugeicons/core-free-icons"
import {
  OVERVIEW_BREAKDOWN_LIMIT,
  OVERVIEW_LIST_CLASS,
} from '@/lib/constants/analyticsOverview'
import {
  OverviewBreakdownCard,
  getPeriodUniqueVisitors,
} from '@/components/shared/overview/OverviewBreakdownCard'

// Loading skeleton for location mixed chart
function LocationSkeleton() {
  return (
    <div className="space-y-2.5 py-2">
      {[0.88, 0.65, 0.52, 0.38, 0.28, 0.18].map((width, i) => (
        <div 
          key={i} 
          className="flex items-center gap-3"
          style={{ 
            animationDelay: `${i * 80}ms`,
            animation: 'fadeInUp 0.35s ease-out forwards',
            opacity: 0
          }}
        >
          {/* Flag placeholder */}
          <div className="w-5 h-5 rounded-full bg-muted/50 animate-pulse shrink-0" />
          
          {/* Bar and label */}
          <div className="flex-1 flex items-center gap-3">
            <div 
              className="h-7 rounded-md bg-gradient-to-r from-muted via-muted/50 to-muted animate-shimmer"
              style={{ 
                width: `${width * 100}%`,
                backgroundSize: '200% 100%'
              }}
            />
          </div>
          
          {/* Value placeholders */}
          <div className="flex items-center gap-2 shrink-0">
            <div className="h-4 w-10 rounded bg-muted/50 animate-pulse" />
            <div className="h-4 w-8 rounded bg-muted/30 animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  )
}

interface LocationComponentProps {
  dashBoardData: any
  onOpenModal?: (modalType: string) => void
  onFilterByParam?: (category: string, filter: string) => void
  className?: string
  /** Overview list-only: muted row bars */
  overviewMode?: boolean
}

type TabType = 'countries' | 'cities' | 'regions'

export default function LocationComponent({
  dashBoardData,
  onOpenModal,
  onFilterByParam,
  className = '',
  overviewMode = false,
}: LocationComponentProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const [currentTab, setCurrentTab] = useState<TabType>('countries')
  const [labels, setLabels] = useState<string[]>([])
  const [values, setValues] = useState<number[]>([])
  /** Parallel filter values (ISO country codes for countries tab) */
  const [filterKeys, setFilterKeys] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Get the base path for navigation
  const getBasePath = () => {
    if (params.workspaceId) return `/workspace/${params.workspaceId}`
    if (params.propertySlugId) return `/p/${params.propertySlugId}`
    if (params.domain) return `/d/${params.domain}`
    return ''
  }
  const basePath = getBasePath()

  useEffect(() => {
    if (dashBoardData) {
      // Each tab reads only its own dimension's rows: city and region rows carry
      // their parent country, so counting every row with a `country` field would
      // inflate country totals (see lib/calculations/breakdownTotals).
      const aggregate = dashBoardData?.metrics?.aggregate
      const countryStats: Record<string, number> = {}
      const cityStats: Record<string, number> = {}
      const regionStats: Record<string, number> = {}
      const withCountry = (name: string, code: string) => `${name}, ${countryCodes[code] || code}`
      aggregateCountries(aggregate).forEach((c) => { countryStats[c.country] = c.count })
      aggregateCities(aggregate).forEach((c) => { cityStats[withCountry(c.name, c.country)] = c.count })
      aggregateRegions(aggregate).forEach((r) => { regionStats[withCountry(r.name, r.country)] = r.count })

      let itemCountArray: [string, any][] = []
      const limit = overviewMode ? OVERVIEW_BREAKDOWN_LIMIT : 7
      
      switch (currentTab) {
        case 'countries':
          itemCountArray = Object.entries(countryStats)
          itemCountArray.sort((a, b) => Number(b[1]) - Number(a[1]))
          setLabels(
            itemCountArray.slice(0, limit).map(([code]) => {
              const countryName = countryCodes[code as string] || code
              const emoji = getCountryEmoji(code as string)
              return `${countryName}  ${emoji}`
            })
          )
          setFilterKeys(itemCountArray.slice(0, limit).map(([code]) => code as string))
          setValues(itemCountArray.slice(0, limit).map(([, count]) => Number(count)))
          break
        
        case 'cities':
          itemCountArray = Object.entries(cityStats)
          itemCountArray.sort((a, b) => Number(b[1]) - Number(a[1]))
          setLabels(itemCountArray.slice(0, limit).map(([city]) => city))
          setFilterKeys(itemCountArray.slice(0, limit).map(([city]) => city))
          setValues(itemCountArray.slice(0, limit).map(([, count]) => Number(count)))
          break
        
        case 'regions':
          itemCountArray = Object.entries(regionStats)
          itemCountArray.sort((a, b) => Number(b[1]) - Number(a[1]))
          setLabels(itemCountArray.slice(0, limit).map(([region]) => region))
          setFilterKeys(itemCountArray.slice(0, limit).map(([region]) => region))
          setValues(itemCountArray.slice(0, limit).map(([, count]) => Number(count)))
          break
      }

      setIsLoading(false)
    }
  }, [dashBoardData, currentTab, overviewMode])

  // Navigate to the full Locations page instead of opening a modal
  const navigateToLocations = () => {
    navigate(`${basePath}/devices${location.search}`)
  }

  const handleFilter = (title: string) => {
    if (!onFilterByParam) return
    const idx = labels.indexOf(title)
    const key = idx >= 0 ? filterKeys[idx] : title
    if (currentTab === 'countries') {
      onFilterByParam('country', key)
    } else if (currentTab === 'cities') {
      onFilterByParam('city', key)
    } else {
      onFilterByParam('region', key)
    }
  }

  if (overviewMode) {
    const overviewTabs: { value: TabType; label: string }[] = [
      { value: 'countries', label: 'Countries' },
      { value: 'cities', label: 'Cities' },
      { value: 'regions', label: 'Regions' },
    ]
    return (
      <OverviewBreakdownCard
        className={className}
        title="Geography"
        tabs={overviewTabs}
        tab={currentTab}
        onTabChange={setCurrentTab}
        dimensionLabel={currentTab === 'countries' ? 'Country' : currentTab === 'cities' ? 'City' : 'Region'}
        valueLabel="Visitors"
        showColumnHeader={false}
        rows={labels.map((label, i) => {
          const key = filterKeys[i] ?? label
          // Plain names in the overview table: no flag emoji
          const name = currentTab === 'countries' ? countryCodes[key] || key : label
          return { label: name, key: label, value: values[i] ?? 0 }
        })}
        total={getPeriodUniqueVisitors(dashBoardData?.metrics)}
        limit={4}
        isLoading={isLoading || !dashBoardData}
        onRowClick={handleFilter}
        footerLabel="View all locations"
        onFooterClick={navigateToLocations}
      />
    )
  }

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="border-b border-border/40 pb-3">
          <div className="flex items-center justify-between gap-3 w-full">
            <div className="flex gap-1">
              <div className="h-7 w-20 rounded-md bg-muted/50 animate-pulse" />
              <div className="h-7 w-16 rounded-md bg-muted/30 animate-pulse" />
              <div className="h-7 w-18 rounded-md bg-muted/30 animate-pulse" />
            </div>
            <div className="h-7 w-16 rounded-md bg-muted/30 animate-pulse" />
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <LocationSkeleton />
        </CardContent>
      </Card>
    )
  }

  const tabs: { value: TabType; label: string }[] = [
    { value: 'countries', label: 'Countries' },
    { value: 'cities', label: 'Cities' },
    { value: 'regions', label: 'Regions' },
  ]

  return (
    <Card className={className}>
      <CardHeader className="border-b border-border/40 pb-3">
        <div className="flex items-center justify-between gap-3 w-full">
          {overviewMode && (
            <span className="text-sm font-medium text-foreground shrink-0">Geography</span>
          )}
          <SegmentedControl
            options={tabs}
            value={currentTab}
            onChange={setCurrentTab}
            className="flex-1 min-w-0"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={navigateToLocations}
          >
            View all
            <HugeiconsIcon icon={ArrowRight01Icon} size={14} className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {labels.length === 0 && dashBoardData ? (
          <div className="dashboard-empty">
            No data to display. Try a different date range.
          </div>
        ) : dashBoardData ? (
          <div className={overviewMode ? OVERVIEW_LIST_CLASS : "w-full flex-1 overflow-y-auto min-h-0"}>
            <LocationMixedChart
              labels={labels}
              values={values}
              onBarClick={(label) => handleFilter(label)}
              maxItems={overviewMode ? OVERVIEW_BREAKDOWN_LIMIT : 7}
              variant="underline"
            />
          </div>
        ) : (
          <LocationSkeleton />
        )}
      </CardContent>
    </Card>
  )
}
